/**
 * Engage chat — defensive JSON parsing for Grok output.
 *
 * Issue: https://github.com/Scale2Rev/scale2rev-platform/issues/99
 * Plan:  .cursor/plans/engage-tsai-parity-migration_e28398f6.plan.md (p0_chat_json_repair)
 * Tribal: docs/legacy/tsai-tribal-knowledge.md — "master_context.txt JSON schema
 *         is hand-parsed ... model occasionally emits markdown fences. No
 *         retry/repair logic — port one in."
 *
 * Pipeline (per #99 acceptance criteria)
 * --------------------------------------
 *   1. JSON.parse the raw model output.
 *   2. On failure: strip leading/trailing markdown fences (```json / ```),
 *      trim, retry parse.
 *   3. On second failure: one defensive model call asking for raw JSON
 *      with the malformed text as context (see `repairChatModelJson`).
 *   4. On a third failure: caller logs to `audit_logs` and returns the
 *      canned refusal text (`FALLBACK_REFUSAL_TEXT`).
 *
 * Module boundaries
 * -----------------
 * - This file is pure (no I/O). The repair step takes an injected callable
 *   so tests can stub the model client without faking the AI SDK.
 * - `extractAssistantPayload` is the unified replacement for the legacy
 *   in-route `parseAssistantPayload`: it returns BOTH the user-visible
 *   text (`responseText`) AND the structured analytics fields.
 */

import type { Json } from '@scale2rev/database';

import type { ResolvedModelCta } from '@/lib/engage/model-cta';

/**
 * Canonical user-visible refusal when JSON repair fails three times in a row.
 * Wording matches the spirit of `data/context/master_context.txt`: "rely on
 * your primary facts or inform the user you do not know."
 */
export const FALLBACK_REFUSAL_TEXT =
  "I don't have enough information to answer that right now. Could you rephrase, or ask me something else?";

/** Matches ```json (case-insensitive) or ``` at the start of the string. */
const LEADING_FENCE_RE = /^\s*```(?:json|JSON)?\s*\r?\n?/;
/** Matches a closing ``` (with optional trailing whitespace) at end of string. */
const TRAILING_FENCE_RE = /\r?\n?\s*```\s*$/;

/**
 * Remove leading/trailing markdown code fences, regardless of language tag.
 * Idempotent: stripping a fence-less string returns it untouched (trimmed).
 */
export function stripMarkdownFences(raw: string): string {
  return raw.replace(LEADING_FENCE_RE, '').replace(TRAILING_FENCE_RE, '').trim();
}

/** Heuristic: does this string look like it was intended to be JSON? */
export function looksLikeJsonAttempt(raw: string): boolean {
  const trimmed = raw.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) return true;
  if (LEADING_FENCE_RE.test(trimmed)) return true;
  if (trimmed.includes('{') && trimmed.includes('"response"')) return true;
  return false;
}

/**
 * Grok sometimes prefixes the required JSON object with prose
 * ("The question is: …"). TSAI assumed the model obeyed "JSON only";
 * extract the first balanced `{…}` object so visitors still see
 * `response.text` instead of the raw blob.
 */
export function extractTopLevelJsonSlice(raw: string): string | null {
  const stripped = stripMarkdownFences(raw.trim());
  const start = stripped.indexOf('{');
  if (start < 0) return null;

  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = start; i < stripped.length; i++) {
    const ch = stripped[i];
    if (inString) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (ch === '\\') {
        escaped = true;
        continue;
      }
      if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === '{') depth += 1;
    else if (ch === '}') {
      depth -= 1;
      if (depth === 0) return stripped.slice(start, i + 1);
    }
  }
  return null;
}

export type ParseResult =
  | { ok: true; value: Record<string, unknown>; strippedFences: boolean }
  | { ok: false; reason: 'not_json_shape' | 'parse_failed'; rawPreview: string };

/**
 * Two-step parse: direct, then with fences stripped.
 *
 * Returns `{ ok: false, reason: 'not_json_shape' }` when the raw text
 * doesn't even look like a JSON attempt — callers should treat this as
 * "the model returned plain prose" and use the raw text as-is.
 */
export function parseChatModelJson(raw: string): ParseResult {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { ok: false, reason: 'not_json_shape', rawPreview: '' };
  }

  if (!looksLikeJsonAttempt(trimmed)) {
    return { ok: false, reason: 'not_json_shape', rawPreview: previewOf(trimmed) };
  }

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return { ok: true, value: parsed as Record<string, unknown>, strippedFences: false };
    }
  } catch {
    /* fall through to fence-stripping retry */
  }

  const stripped = stripMarkdownFences(trimmed);
  if (stripped && stripped !== trimmed) {
    try {
      const parsed = JSON.parse(stripped) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return { ok: true, value: parsed as Record<string, unknown>, strippedFences: true };
      }
    } catch {
      /* fall through */
    }
  }

  const embedded = extractTopLevelJsonSlice(trimmed);
  if (embedded) {
    try {
      const parsed = JSON.parse(embedded) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return { ok: true, value: parsed as Record<string, unknown>, strippedFences: false };
      }
    } catch {
      /* fall through */
    }
  }

  if (trimmed.includes('{')) {
    return { ok: false, reason: 'parse_failed', rawPreview: previewOf(trimmed) };
  }

  return { ok: false, reason: 'not_json_shape', rawPreview: previewOf(trimmed) };
}

/**
 * Caller-provided model invoker for the repair step. Returns the raw text
 * the model produced (without any post-processing). Kept narrow so tests
 * can stub it with a single Promise.
 */
export type ModelInvoker = (args: {
  systemPrompt: string;
  userPrompt: string;
}) => Promise<string>;

export const REPAIR_SYSTEM_PROMPT =
  'You repair malformed JSON. Output ONLY a single valid JSON object that matches the original intent. No prose, no markdown, no commentary. If the input is unrecoverable, return {}.';

/**
 * Defensive third pass: ask the model to re-emit the same content as a
 * single valid JSON object. Only invoke this when `parseChatModelJson`
 * returned `{ ok: false, reason: 'parse_failed' }` — i.e. the original
 * output looked like a JSON attempt and we exhausted fence-strip.
 *
 * The invoker itself decides which model to use; this lib stays
 * model-agnostic.
 */
export async function repairChatModelJson(
  rawMalformed: string,
  invoke: ModelInvoker,
): Promise<ParseResult> {
  let repairedText: string;
  try {
    repairedText = await invoke({
      systemPrompt: REPAIR_SYSTEM_PROMPT,
      userPrompt: `Return ONLY valid JSON, no markdown.\n\nORIGINAL OUTPUT:\n${rawMalformed}`,
    });
  } catch (e) {
    return {
      ok: false,
      reason: 'parse_failed',
      rawPreview: `repair_invoke_threw: ${String(e instanceof Error ? e.message : e)}`,
    };
  }
  return parseChatModelJson(repairedText ?? '');
}

export interface ResolveEngageChatAssistantPayloadInput {
  /** Raw model output before CTA post-process (used as repair context). */
  readonly rawText: string;
  /** Model output after {@link applyModelCtaPostProcessToRawText}. */
  readonly postProcessedRaw: string;
  /** When set, one Grok repair call runs on `parseHadFailure` (legacy + stream persist). */
  readonly invokeRepair?: ModelInvoker;
  /**
   * One full model re-query with the original turn (not the JSON-repair prompt).
   * Runs only when parse failed and repair did not recover a payload.
   */
  readonly invokeRetry?: () => Promise<string>;
  /** CTA post-process for {@link invokeRetry} raw output. */
  readonly postProcessRetry?: (raw: string) => string;
}

export interface ResolveEngageChatAssistantPayloadResult {
  readonly extracted: ExtractedPayload;
  readonly repairUsed: boolean;
  readonly repairSucceeded: boolean;
  readonly retryUsed: boolean;
  readonly retrySucceeded: boolean;
  /** Set when repair was attempted but parse still failed (for audit_logs). */
  readonly repairFailedPreview?: string;
  /** First malformed preview — keep even if a later retry also fails. */
  readonly originalRawPreview?: string;
}

/**
 * Shared JSON repair pipeline (#99 / #1058): extract → optional model repair →
 * optional one full re-query → fallback refusal.
 * Stream and legacy callers must send the recovered `extracted.responseText`
 * (or a valid visitor JSON envelope) — never the raw malformed model bytes.
 */
export async function resolveEngageChatAssistantPayload(
  input: ResolveEngageChatAssistantPayloadInput,
): Promise<ResolveEngageChatAssistantPayloadResult> {
  let extracted = extractAssistantPayload(input.postProcessedRaw);
  let repairUsed = false;
  let repairSucceeded = false;
  let retryUsed = false;
  let retrySucceeded = false;
  let repairFailedPreview: string | undefined;
  const originalFailurePreview = extracted.parseHadFailure
    ? extracted.rawPreview
    : undefined;

  if (extracted.parseHadFailure && input.invokeRepair) {
    repairUsed = true;
    const repairResult = await repairChatModelJson(input.rawText, input.invokeRepair);
    if (repairResult.ok) {
      repairSucceeded = true;
      extracted = extractAssistantPayload(JSON.stringify(repairResult.value));
    } else {
      repairFailedPreview = repairResult.rawPreview;
    }
  }

  if (extracted.parseHadFailure && input.invokeRetry) {
    retryUsed = true;
    try {
      const retryRaw = await input.invokeRetry();
      const retryPost = input.postProcessRetry?.(retryRaw) ?? retryRaw;
      extracted = extractAssistantPayload(retryPost);
      if (!extracted.parseHadFailure) {
        retrySucceeded = true;
      } else if (input.invokeRepair) {
        const retryRepair = await repairChatModelJson(retryRaw, input.invokeRepair);
        if (retryRepair.ok) {
          retrySucceeded = true;
          extracted = extractAssistantPayload(JSON.stringify(retryRepair.value));
        } else {
          repairFailedPreview = repairFailedPreview ?? retryRepair.rawPreview;
        }
      }
    } catch (e) {
      repairFailedPreview =
        repairFailedPreview
        ?? `retry_invoke_threw: ${String(e instanceof Error ? e.message : e)}`;
    }
  }

  if (extracted.parseHadFailure && !repairFailedPreview) {
    repairFailedPreview = originalFailurePreview;
  }

  if (extracted.parseHadFailure) {
    extracted = {
      ...extracted,
      responseText: FALLBACK_REFUSAL_TEXT,
      parseHadFailure: false,
    };
  }

  return {
    extracted,
    repairUsed,
    repairSucceeded,
    retryUsed,
    retrySucceeded,
    repairFailedPreview,
    originalRawPreview: originalFailurePreview,
  };
}

/**
 * The unified replacement for the in-route `parseAssistantPayload`.
 *
 * Returns:
 *   - `responseText`: what the widget should display to the user. When the
 *     model emitted structured JSON with a `response` key, this is that
 *     `response` (unwrapped). Otherwise it's the (de-fenced) raw text so
 *     visitors never see ``` ```json ``` ``` literals.
 *   - `analysis`, `sentiment`, `lead`, `contact*`: best-effort extraction.
 *   - `parseHadFailure`: true when the model looked like it WAS attempting
 *     JSON but neither parse pass succeeded — the caller should consider
 *     invoking `repairChatModelJson` (legacy path only) and/or logging.
 */
export interface ExtractedPayload {
  responseText: string;
  analysis: Json | null;
  sentiment: string | null;
  lead: number | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  contactCount: number;
  parseHadFailure: boolean;
  rawPreview: string;
  cta: ResolvedModelCta | null;
}

export function extractAssistantPayload(rawText: string): ExtractedPayload {
  const empty: ExtractedPayload = {
    responseText: stripMarkdownFences(rawText),
    analysis: null,
    sentiment: null,
    lead: null,
    contactName: null,
    contactEmail: null,
    contactPhone: null,
    contactCount: 0,
    parseHadFailure: false,
    rawPreview: previewOf(rawText),
    cta: null,
  };

  const result = parseChatModelJson(rawText);
  if (!result.ok) {
    return {
      ...empty,
      parseHadFailure: result.reason === 'parse_failed',
    };
  }

  return { ...empty, ...payloadFromObject(result.value), parseHadFailure: false };
}

/**
 * Build the structured slice of an `ExtractedPayload` from an already-parsed
 * JSON object. Exported so callers (e.g. the repair path) can merge a
 * repaired value into the same shape without re-parsing.
 */
export function payloadFromObject(
  parsed: Record<string, unknown>,
): Pick<
  ExtractedPayload,
  | 'responseText'
  | 'analysis'
  | 'sentiment'
  | 'lead'
  | 'contactName'
  | 'contactEmail'
  | 'contactPhone'
  | 'contactCount'
  | 'cta'
> {
  let analysis: Json | null = null;
  let sentiment: string | null = null;
  let lead: number | null = null;
  let contactName: string | null = null;
  let contactEmail: string | null = null;
  let contactPhone: string | null = null;

  if (parsed.analysis) {
    analysis = parsed.analysis as Json;
    const analysisObj = parsed.analysis as Record<string, unknown>;
    const s = analysisObj?.sentiment;
    if (typeof s === 'string') sentiment = s;
    const lc = analysisObj?.lead_confidence;
    if (typeof lc === 'number') lead = lc;
  }

  const contactSource =
    (parsed.analysis as Record<string, unknown> | undefined)?.contact ??
    parsed.contact ??
    (parsed.response as Record<string, unknown> | undefined)?.contact ??
    null;
  if (contactSource && typeof contactSource === 'object') {
    const co = contactSource as Record<string, unknown>;
    contactName = typeof co.name === 'string' && co.name.trim() ? co.name.trim() : null;
    contactEmail = typeof co.email === 'string' && co.email.trim() ? co.email.trim() : null;
    contactPhone = typeof co.phone === 'string' && co.phone.trim() ? co.phone.trim() : null;
  }

  let responseText = extractNestedResponseText(parsed);
  if (!responseText) {
    // Last-resort: emit the entire blob so the operator notices in product
    // and the AI can be re-prompted; better than empty bubble.
    responseText = JSON.stringify(parsed);
  }

  const contactCount = [contactName, contactEmail, contactPhone].filter(Boolean).length;
  return {
    responseText,
    analysis,
    sentiment,
    lead,
    contactName,
    contactEmail,
    contactPhone,
    contactCount,
    cta: readResolvedCta(parsed.cta),
  };
}

function readResolvedCta(raw: unknown): ResolvedModelCta | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const label = typeof row.label === 'string' && row.label.trim() ? row.label.trim() : '';
  const url = typeof row.url === 'string' && row.url.trim() ? row.url.trim() : '';
  if (!label || !url || !/^https?:\/\//i.test(url)) return null;
  return { label, url };
}

/**
 * Grok occasionally nests `response.response.text` (debug/citation mode).
 * Walk the chain and prefer the longest non-empty `text` leaf.
 */
export function extractNestedResponseText(parsed: Record<string, unknown>): string {
  if (typeof parsed.response === 'string') {
    return parsed.response.trim();
  }

  let best = '';
  let node: unknown = parsed.response;
  for (let depth = 0; depth < 8 && node && typeof node === 'object' && !Array.isArray(node); depth++) {
    const row = node as Record<string, unknown>;
    if (typeof row.text === 'string') {
      const candidate = row.text.trim();
      if (candidate.length > best.length) {
        best = candidate;
      }
    }
    if (row.response === undefined) break;
    node = row.response;
  }
  return best;
}

function previewOf(s: string): string {
  const trimmed = s.trim();
  return trimmed.length > 240 ? `${trimmed.slice(0, 240)}…` : trimmed;
}
